import React, {ReactNode, useMemo} from 'react';

import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {RuleActionTypesEnum} from '../../../ruleActionModel';
import {RuleActionCardProps} from '../../ruleActionCard';
import {RuleActionsList} from '../../ruleActionsList';
import {useRuleActionGroups} from '../../ruleEditorActions';
import {RuleEditorSection} from '../../ruleEditorSection';
import {useRuleActionWithParams} from '../helpers/recipeHooksHelpers';
import {buildRuleActionCardsProps} from '../helpers/recipesHelpers';
import {CsatEditorStateActions} from './hooks/useCsatEditorState';
import {CsatEditorState} from './state/csatEditorState';

/*
 * Props.
 */

interface CsatEditorScheduledActionsProps {
  state: CsatEditorState;
  description: ReactNode;

  actions: CsatEditorStateActions;
}

/*
 * Component.
 */

export const CsatEditorScheduledActions: React.FC<CsatEditorScheduledActionsProps> = (props) => {
  const {state, description} = props;

  const {ruleIntegration} = useRuleEditorContext();
  const actionGroups = useRuleActionGroups(ruleIntegration);

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();

  const tagActionParam = useMemo(
    () => ({
      tag: state.csatState.tags.scheduled,
    }),
    [state.csatState.tags.scheduled],
  );

  const tagAction = useRuleActionWithParams(RuleActionTypesEnum.TAG, tagActionParam);

  const tagActionProps: RuleActionCardProps = {
    action: tagAction,
    actionDefinition: ruleIntegration.findActionDefinition(tagAction),
    onUpdateAction: (action) => {
      if (action.type !== RuleActionTypesEnum.TAG) {
        return;
      }

      if (action.params.tag) {
        props.actions.onSetScheduledTag(action.params.tag);
      }
    },
    onValidation: props.actions.onValidation,
  };

  const actions = buildRuleActionCardsProps(
    state.csatState.actions.scheduled,
    ruleIntegration,
    props.actions.onUpdateScheduledAction,
    props.actions.onValidation,
    props.actions.onDeleteScheduledAction,
  );

  return (
    <RuleEditorSection
      description={description}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      <RuleActionsList
        actions={[tagActionProps, ...actions]}
        actionGroups={actionGroups}
        onAddAction={props.actions.onAddScheduledAction}
        shouldShowMoreOptions={shouldShowMoreOptions}
      />
    </RuleEditorSection>
  );
};
